import type { ReasoningEffort } from "@/types/chat.type";
import { cn } from "@/lib/utils";

export const REASONING_LABELS: Record<ReasoningEffort, string> = {
  none: "Off",
  minimal: "Minimal",
  low: "Low",
  medium: "Medium",
  high: "High",
  xhigh: "xHigh",
  max: "Max",
};

export const REASONING_COLORS: Record<ReasoningEffort, string> = {
  none: "text-co-faint",
  minimal: "text-slate-400",
  low: "text-emerald-400",
  medium: "text-amber-400",
  high: "text-orange-400",
  xhigh: "text-rose-400",
  max: "text-violet-400",
};

/** Button padding + chevron + gap beyond label text. */
export const SELECT_CHROME_PX = 30;
export const ICON_ONLY_SELECT_PX = 22;
export const MODEL_HINT_WIDTH_PX = 220;
export const MODEL_HINT_GAP_PX = 8;

export const SELECT_MEASURE_FONT =
  '500 10px "Helvetica Now Display", "Helvetica Neue", Helvetica, Arial, sans-serif';

export const compactSelectPopoverClass = cn(
  "[&_[role=option]]:text-[11px] [&_[role=option]]:text-co-muted",
  "[&_[role=option][data-hovered]]:bg-co-hover [&_[role=option][data-hovered]]:text-co-text",
  "[&_[role=option][data-focused]]:bg-co-hover [&_[role=option][data-focused]]:text-co-text",
  "[&_[role=option][data-selected]]:text-co-text",
  "[&_[role=option]_svg.ms-auto]:size-3",
  "[&_[role=option]:focus-visible]:!shadow-none [&_[role=option]:focus-visible]:!outline-none",
  "[&_header]:border-none [&_header]:bg-transparent [&_header]:px-2 [&_header]:py-1",
  "[&_header]:text-[9px] [&_header]:font-semibold [&_header]:uppercase [&_header]:tracking-wider [&_header]:text-co-faint",
);

export const reasoningSelectPopoverClass = cn(
  "[&_[role=option]]:text-[11px]",
  "[&_[role=option][data-hovered]]:bg-co-hover",
  "[&_[role=option][data-focused]]:bg-co-hover",
  "[&_[role=option]_svg.ms-auto]:size-3",
  "[&_[role=option]:focus-visible]:!shadow-none [&_[role=option]:focus-visible]:!outline-none",
);

export type ModelHintAnchor = {
  id: string;
  top: number;
  left: number;
  height: number;
};

export function getOptionRectFromTarget(
  target: EventTarget | null,
): Pick<ModelHintAnchor, "top" | "left" | "height"> | null {
  if (!(target instanceof HTMLElement)) return null;
  const option =
    target.getAttribute("role") === "option"
      ? target
      : target.closest('[role="option"]');
  if (!option) return null;
  const rect = option.getBoundingClientRect();
  return { top: rect.top, left: rect.left, height: rect.height };
}

export function measureSelectLabelWidth(label: string) {
  if (typeof document === "undefined") {
    return label.length * 6;
  }
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return label.length * 6;
  ctx.font = SELECT_MEASURE_FONT;
  return ctx.measureText(label).width;
}

export function defaultReasoningEffort(
  options: ReasoningEffort[] | null | undefined,
): ReasoningEffort {
  if (!options?.length) return "low";
  if (options.includes("low")) return "low";
  return options[0];
}

/** Prefer a saved effort when the model still supports it; else fall back. */
export function resolveReasoningEffort(
  options: ReasoningEffort[] | null | undefined,
  preferred: ReasoningEffort | null | undefined,
): ReasoningEffort {
  if (preferred && options?.includes(preferred)) return preferred;
  return defaultReasoningEffort(options);
}
